import { createContext, PropsWithChildren, useContext, useRef } from "react";
import {
  createStore,
  StateCreator,
  StoreApi,
  useStore as useZustandStore,
} from "zustand";

/**
 * Zustand , Context 기반 Store를 생성 함수
 * - Context.Provider로 상태 범위 관리 가능
 * - 내부적으로 zustand의 store를 사용 리렌더 최적화
 */
export const createZustandContext = <TStore extends object>(
  createFn: StateCreator<TStore>,
) => {
  // zustand store를 담을 Context
  const ZustandStoreContext = createContext<StoreApi<TStore> | null>(null);

  const Provider = ({ children }: PropsWithChildren) => {
    const storeRef = useRef<StoreApi<TStore> | null>(null);

    // store는 최초 1회만 생성
    if (!storeRef.current) {
      storeRef.current = createStore(createFn);
    }

    return (
      <ZustandStoreContext.Provider value={storeRef.current}>
        {children}
      </ZustandStoreContext.Provider>
    );
  };

  // 상태변경은 zustand 의 useStore 내부 selector 사용하도록
  const useStore = <U,>(selector: (store: TStore) => U): U => {
    //store provider 내부에서만 사용하도록 제한
    const store = useContext(ZustandStoreContext);
    if (!store) throw new Error("useStore must be used within Provider");

    return useZustandStore(store, selector);
  };

  return { Provider, useStore };
};
